/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useLazyFetchMenuGroupListQuery } from "queries/menuApi"
import { FetchMenuGroupListResponse } from "types/api"
import {
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
  TroyWebTracker,
} from "utils/tracking"
import { actions } from "store/duck/menu"
import { getActionSuccess } from "store/duck/utils/actionUtils"
import {
  EventAction,
  EventCategory,
  EventCategoryEng,
  EventActionEng,
} from "constants/tracking"

export interface MenuGroupListFetchParams {
  menuGroupName?: string
  isWithItemPhotoCount?: boolean
}

interface MenuGroupListRequestParams extends MenuGroupListFetchParams {
  merchantGroupID: string
}

/**
 * Shared menu-group-list fetching for the menu groups page and the
 * "choose existing menu" drawer. Both run the same RTK-migration path
 * with the same tracking events; only the page-view category differs.
 */
export const useMenuGroupListFetcher = (pageViewCategory: EventCategory) => {
  const dispatch = useDispatch()

  const merchantGroupID = useSelector(
    (state: AppState) => state.menu.merchantGroupID,
  )

  const {
    flags: { fetchMenuGroupList: isRtkFetchMenuGroupListEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [fetchMenuGroupListRtk] = useLazyFetchMenuGroupListQuery()

  const sendRtkMigrationEvent = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchMenuGroupList,
      labels: {
        MERCHANT_ID: merchantGroupID,
        IS_RTK_ENABLED: isRtkFetchMenuGroupListEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "fetchMenuGroupList",
      },
    })
  }

  // Mirror the legacy Redux SUCCESS action so reducers see the same shape.
  const dispatchMenuGroupListSuccess = (
    data: FetchMenuGroupListResponse,
    { menuGroupName }: MenuGroupListRequestParams,
  ) => {
    dispatch({
      type: getActionSuccess(actions.FETCH_MENU_GROUP_LIST),
      payload: {
        data,
      },
      meta: {
        previousAction: {
          type: actions.FETCH_MENU_GROUP_LIST,
          payload: {
            request: {
              url: `/v1/menu-groups`,
              method: "get",
              headers: { merchantGroupID },
              data: { menuGroupName },
            },
          },
        },
      },
    })
  }

  /** Initial load: dispatches PENDING/SUCCESS and tracks load time plus a page-view event. */
  const fetchMenuGroupList = async (params: MenuGroupListFetchParams = {}) => {
    const requestParams: MenuGroupListRequestParams = {
      merchantGroupID,
      ...params,
    }
    if (!isRtkFlagLoading && isRtkFetchMenuGroupListEnabled) {
      // 1. Dispatch PENDING action
      dispatch({
        type: actions.FETCH_MENU_GROUP_LIST,
      })

      const t0 = performance.now()
      const result = await fetchMenuGroupListRtk(requestParams)
      const t1 = performance.now()
      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        LOAD_TIME: t1 - t0,
        LOADING_STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }

      // 2. Dispatch SUCCESS action with data to update Redux state
      if (result?.data) {
        dispatchMenuGroupListSuccess(result.data, requestParams)
      }

      if (result?.error) {
        labels = {
          ...labels,
          LOADING_STATUS: "FAILURE",
          ...getResponseErrorEventLabelsForRTK(result),
        }
      }
      TroyWebTracker.sendEvent({
        category: pageViewCategory,
        action: EventAction.pageView,
        labels,
      })
      sendRtkMigrationEvent(true)
    } else if (!isRtkFlagLoading) {
      const t0 = performance.now()
      const resp = await dispatch(
        actionCreator.menu.fetchMenuGroupList(requestParams),
      )
      const t1 = performance.now()
      let labels = {
        MERCHANT_GROUP_ID: merchantGroupID,
        LOAD_TIME: t1 - t0,
        LOADING_STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }
      if (resp?.type?.endsWith("_FAIL")) {
        labels = {
          ...labels,
          LOADING_STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }
      TroyWebTracker.sendEvent({
        category: pageViewCategory,
        action: EventAction.pageView,
        labels,
      })
      sendRtkMigrationEvent(false)
    }
  }

  /** Search by menu-group name: no load-time or page-view tracking, matching the legacy search handlers. */
  const searchMenuGroups = async (menuGroupName: string) => {
    const requestParams: MenuGroupListRequestParams = {
      merchantGroupID,
      menuGroupName,
    }
    if (!isRtkFlagLoading && isRtkFetchMenuGroupListEnabled) {
      // 1. Dispatch PENDING action
      dispatch({
        type: actions.FETCH_MENU_GROUP_LIST,
      })

      const result = await fetchMenuGroupListRtk(requestParams)

      // 2. Dispatch SUCCESS action with data to update Redux state
      if (result?.data) {
        dispatchMenuGroupListSuccess(result.data, requestParams)
      }

      sendRtkMigrationEvent(true)
    } else if (!isRtkFlagLoading) {
      dispatch(actionCreator.menu.fetchMenuGroupList(requestParams))
      sendRtkMigrationEvent(false)
    }
  }

  return {
    fetchMenuGroupList,
    searchMenuGroups,
    isRtkFlagLoading,
    isRtkFetchMenuGroupListEnabled,
  }
}
